import { useGpcCategoryLocales } from '~/features/configuration/hooks/useGpcCategoryLocales';

/** Locale whose spelling we surface for a resolved GPC id. */
const GPC_DISPLAY_LOCALE = 'en-US';

/** Shown for a numeric category that resolves to no en-US name (missing row or a
 *  failed lookup) — never the bare number. Matches the app's not-available dash. */
const CATEGORY_PLACEHOLDER = '—';

export interface ProductCategoryDisplay {
  /** The string to render: the en-US category name for a numeric GPC id, the
   *  original text for a path, `—` when a numeric id can't be resolved, or `''`
   *  while a numeric id is still being fetched. */
  display: string;
  /** True only while a numeric id's name is in flight — lets callers show a skeleton. */
  isResolving: boolean;
}

/**
 * Resolves a product's category for display. A numeric GPC id (e.g. `187`) is
 * looked up in the shared, permanently-cached `gpc_categories` table and shown as
 * its en-US category name; a text path passes through unchanged; an unresolved id
 * shows a dash rather than the raw number.
 */
export function useProductCategoryDisplay(
  value: string | number | null | undefined,
): ProductCategoryDisplay {
  const raw = value == null ? '' : String(value).trim();
  // Only bare integers are GPC ids; a real path ("Apparel > Shoes") is left as-is.
  const isNumericId = raw.length > 0 && /^\d+$/.test(raw);
  const { data, isLoading } = useGpcCategoryLocales(isNumericId ? Number(raw) : null);

  if (!isNumericId) return { display: raw, isResolving: false };
  if (isLoading) return { display: '', isResolving: true };

  const name = data?.rows.find((row) => row.locale === GPC_DISPLAY_LOCALE)?.categoryName ?? null;
  return { display: name ?? CATEGORY_PLACEHOLDER, isResolving: false };
}
